import React from "react";
import { useNavigate } from "react-router-dom";
import { Stethoscope, NotebookPen, BookOpen, Download, LifeBuoy, ArrowRight, Activity, CalendarCheck, AlertCircle } from "lucide-react";
import AppLayout from "../components/AppLayout";
import { useAsaAuth } from "../AuthContext";
import { ARTICLES } from "../data";

function readDiary() {
  try { return JSON.parse(localStorage.getItem("asa_diary") || "[]"); } catch { return []; }
}
function readLastCheck() {
  try { return JSON.parse(localStorage.getItem("asa_last_check") || "null"); } catch { return null; }
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAsaAuth();
  const diary = readDiary();
  const lastCheck = readLastCheck();
  const flares = diary.filter((d) => d.flare).length;

  const actions = [
    { icon: Stethoscope, title: "Symptom Checker", desc: "Check symptoms & family history", to: "/asa/symptom-checker", tint: "#0d9488" },
    { icon: NotebookPen, title: "Symptom Diary", desc: "Log today's symptoms", to: "/asa/diary", tint: "#2b7fd9" },
    { icon: BookOpen, title: "Education Library", desc: "Learn & manage better", to: "/asa/library", tint: "#e08a2b" },
    { icon: Download, title: "Reports & Tools", desc: "GP reports & exports", to: "/asa/reports", tint: "#6d4bd8" },
  ];

  return (
    <AppLayout title="Dashboard">
      <div className="bg-gradient-to-r from-[#0d9488] to-[#0f766e] rounded-2xl p-6 text-white">
        <h2 className="font-head font-bold text-2xl">Hello, {user?.name?.split(" ")[0] || "there"} 👋</h2>
        <p className="text-white/85 mt-1">Here's a snapshot of your autoimmune health today.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-5">
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#e6f4f2] text-[#0d9488] flex items-center justify-center"><CalendarCheck size={22} /></div>
          <div><p className="text-2xl font-head font-bold text-slate-800">{diary.length}</p><p className="text-[13px] text-slate-500">Diary entries</p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#fdeceb] text-[#e0567a] flex items-center justify-center"><Activity size={22} /></div>
          <div><p className="text-2xl font-head font-bold text-slate-800">{flares}</p><p className="text-[13px] text-slate-500">Episodes logged</p></div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5 flex items-center gap-4">
          <div className="w-11 h-11 rounded-lg bg-[#eef1fb] text-[#6d4bd8] flex items-center justify-center"><Stethoscope size={22} /></div>
          <div><p className="text-2xl font-head font-bold text-slate-800">{lastCheck ? "Done" : "—"}</p><p className="text-[13px] text-slate-500">Last awareness check</p></div>
        </div>
      </div>

      <h3 className="font-head font-semibold text-slate-800 mt-8 mb-3">Quick actions</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {actions.map((a) => (
          <button key={a.to} onClick={() => navigate(a.to)} className="text-left bg-white rounded-xl border border-slate-200 p-5 hover:shadow-md transition-shadow group">
            <div className="w-11 h-11 rounded-lg flex items-center justify-center mb-3" style={{ backgroundColor: a.tint + "1a", color: a.tint }}><a.icon size={22} /></div>
            <h4 className="font-semibold text-slate-800">{a.title}</h4>
            <p className="text-[13px] text-slate-500 mt-0.5">{a.desc}</p>
            <span className="inline-flex items-center gap-1 text-[13px] font-semibold mt-2 text-[#0d9488]">Open <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" /></span>
          </button>
        ))}
      </div>

      <div className="flex items-center justify-between mt-8 mb-3">
        <h3 className="font-head font-semibold text-slate-800">From the library</h3>
        <button onClick={() => navigate("/asa/library")} className="text-[13px] font-semibold text-[#0d9488] hover:underline">View all</button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {ARTICLES.slice(0, 3).map((a) => (
          <button key={a.id} onClick={() => navigate("/asa/library")} className="text-left bg-white rounded-xl border border-slate-200 overflow-hidden hover:shadow-md transition-shadow">
            <img src={a.image} alt={a.title} className="w-full h-32 object-cover" />
            <div className="p-4"><span className="text-[11px] font-semibold text-[#0d9488]">{a.category}</span><h4 className="font-semibold text-slate-800 text-[15px] mt-1 leading-snug">{a.title}</h4></div>
          </button>
        ))}
      </div>

      <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-4 mt-8">
        <AlertCircle size={18} className="text-amber-500 shrink-0 mt-0.5" />
        <p className="text-[13px] text-amber-700">This app provides general information and support only. It is not a medical diagnosis — always consult a qualified healthcare professional.</p>
      </div>
    </AppLayout>
  );
}
